import React, { createContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { getDoc, doc } from 'firebase/firestore';
import { DEFAULT_PERMISSIONS } from '../utils/permissions';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userHierarchy, setUserHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserRole = async (uid) => {
    try {
      // Always clear cache and fetch fresh from Firestore
      localStorage.removeItem(`userRole_${uid}`);

      const snapshot = await getDoc(doc(db, 'users', uid));
      if (snapshot.exists()) {
        const userData = snapshot.data();
        const currentRole = userData.currentRole || 'member';
        setUserRole(currentRole);
        setUserHierarchy({
          role: currentRole,
          assignedCell: userData.assignedCell,
          assignedChapter: userData.assignedChapter,
          assignedComb: userData.assignedComb,
          assignedColony: userData.assignedColony,
          assignedTerritory: userData.assignedTerritory,
          mountainOfInfluence: userData.mountainOfInfluence,
          permissions: userData.permissions,
        });
        return userData;
      } else {
        console.warn('User document not found in Firestore.');
        setUserRole('member');
        return null;
      }
    } catch (err) {
      console.error('Error fetching user role:', err);
      setError(err.message);
      setUserRole('error');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setCurrentUser(user);
          await fetchUserRole(user.uid);
        } else {
          setCurrentUser(null);
          setUserRole(null);
          setUserHierarchy(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const hasPermission = (permissionName) => {
    if (userRole === 'master_admin') return true;
    if (userRole === 'admin') return true;
    if (!userHierarchy?.permissions) return false;
    return userHierarchy.permissions[permissionName] || false;
  };

  const hasRole = (roles) => {
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    return rolesArray.includes(userRole);
  };

  const isAdminOrAbove = () => {
    return ['master_admin', 'admin'].includes(userRole);
  };

  const isModeratorOrAbove = () => {
    return ['master_admin', 'admin', 'moderator'].includes(userRole);
  };

  const value = {
    currentUser,
    userRole,
    userHierarchy,
    loading,
    error,
    hasPermission,
    hasRole,
    isAdminOrAbove,
    isModeratorOrAbove,
    fetchUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
